<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.navExpanded]: isNavExpanded
      }
    ]"
  >
    <TheGlobalModals />
    <KycWallModal />
    <KYCUltimatumModal
      v-if="showKYCModal"
      @close="showKYCModal = false"
    />
    <FloatingBanner />
    <TheFlashMessage />
    <transition appear>
      <NavMain :class="$style.nav" />
    </transition>
    <ScrollContainer>
      <template #header>
        <transition appear>
          <Header
            v-if="!isBlocked"
            :class="$style.header"
          />
          <BlockedUserHeader
            v-else
            :class="$style.header"
          />
        </transition>
      </template>
      <template #page>
        <router-view :class="$style.page" />
      </template>
    </ScrollContainer>
  </div>
</template>

<script lang="ts">
import { useStorage } from '@vueuse/core';
import { mapState } from 'pinia';
import { defineComponent, nextTick, watch } from 'vue';
import KycWallModal from '@/components/KycWallModal.vue';
import dayjs from '@/dayjs';
import { flash, hideTooltip, modal } from '@/helpers/ui';
import i18n from '@/i18n';
import KYCUltimatumModal from '@/modules/treatwell/modals/KYCUltimatumModal.vue';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useTreatwellStore } from '@/stores/treatwell';
import { PaymentGatewayStatus } from '@/types';
import { useAutoReload } from './auto-reload';
import FloatingBanner from './floating-banner/index.vue';
import TheGlobalModals from './global-modals/index.vue';
import { useGlobalSubscription } from './global-subscription';
import BlockedUserHeader from './header/BlockedUserHeader.vue';
import Header from './header/index.vue';
import { useIntercom } from './intercom';
import NavMain from './nav-main/index.vue';
import ScrollContainer from './ScrollContainer.vue';
import TheFlashMessage from './TheFlashMessage.vue';

let modalObserver;

export default defineComponent({
  name: 'LoggedIn',
  components: {
    FloatingBanner,
    BlockedUserHeader,
    Header,
    TheFlashMessage,
    NavMain,
    TheGlobalModals,
    ScrollContainer,
    KYCUltimatumModal,
    KycWallModal,
  },
  setup() {
    useIntercom();

    const { startSubscription } = useGlobalSubscription();
    startSubscription();

    const seenMarketLaunchIntercomWarning = useStorage(
      'seen_market_launch_intercom_warning',
      false,
    );

    useAutoReload();

    return {
      seenMarketLaunchIntercomWarning,
    };
  },
  data() {
    return {
      showKYCModal: false,
    };
  },
  watch: {
    location: {
      handler() {
        const { isTreatwellUser } = useCompanyStore();
        document.title = `${this.multiLocation ? `${this.location.internalName} - ` : ''}${this.company.name} - ${isTreatwellUser ? 'Treatwell' : 'Salonized'}`;
      },
      immediate: true,
    },
    treatwellKycFetchError: {
      handler(show) {
        if (show) {
          nextTick(() => {
            modal('warning', {
              message: i18n.t('treatwell.kycStatusError.title'),
              subMessage: i18n.t('treatwell.kycStatusError.body'),
            }).then(() => {
              this.dismissTreatwellKycFetchError();
            });
          });
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useCompanyStore, [
      'company',
      'multiLocation',
      'companyNeedsType',
      'isMarketLaunchCountry',
      'isTrial',
      'isTrialExpired',
      'isBlocked',
      'isTreatwellUser',
    ]),
    ...mapState(useLocationsStore, ['location']),
    ...mapState(usePageLayoutStore, [
      'isMobileDesktop',
      'isModalOpen',
      'isNavExpanded',
    ]),
    ...mapState(useTreatwellStore, [
      'shouldShowKYCUltimatum',
      'treatwellKycFetchError',
    ]),
  },
  methods: {
    dismissTreatwellKycFetchError() {
      useTreatwellStore().dismissTreatwellKycFetchError();
    },
    onScroll() {
      hideTooltip();
    },
  },
  created() {
    document.body.addEventListener('scroll', this.onScroll);

    const modalContainer = document.getElementById('modals');
    if (modalContainer) {
      const pageLayoutStore = usePageLayoutStore();
      modalObserver = new MutationObserver(() => {
        pageLayoutStore.isModalOpen = modalContainer.hasChildNodes();
      });

      modalObserver.observe(modalContainer, { childList: true });
    }
  },
  mounted() {
    this.showKYCModal = this.shouldShowKYCUltimatum;
    const mollieErrorRemindMeLaterAt = useStorage(
      'mollie_error_remind_me_later_at',
      0,
    );
    if (
      this.company.paymentGatewayStatus === PaymentGatewayStatus.MollieError &&
      dayjs().diff(mollieErrorRemindMeLaterAt.value, 'day', true) > 3
    ) {
      modal('mollie-error');
    }

    if (!this.seenMarketLaunchIntercomWarning && this.isMarketLaunchCountry) {
      const intercomDivObjects = document.getElementsByClassName(
        'intercom-lightweight-app',
      );
      if (intercomDivObjects.length !== 0) {
        intercomDivObjects[0].addEventListener('click', () => {
          modal('warning', {
            message: this.$t('intercom.market_launch_warning_modal.message'),
            subMessage: this.$t(
              'intercom.market_launch_warning_modal.sub_message',
            ),
          });
          this.seenMarketLaunchIntercomWarning = true;
        });
      }
    }
  },
  beforeUnmount() {
    document.body.removeEventListener('scroll', this.onScroll);
    modalObserver.disconnect();
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
  transition: padding 0.3s $easeOutExpo;

  &:not(.smallScreen) {
    padding-left: $nav-width;

    &.navExpanded {
      padding-left: $nav-width-expanded;
    }
  }
}

.page {
  .base.smallScreen & {
    padding-top: 50px;
    height: 100%;
  }

  .base.modalOpen & {
    z-index: 899;
  }
}

.nav,
.header {
  &:global(.v-enter-active) {
    transition: transform 0.2s $easeOutExpo;
  }
}

.nav {
  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    transform: translateX(-100%);
  }
}

.header {
  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    transform: translateY(-100%);
  }
}

@media print {
  .base {
    &:not(.smallScreen) {
      padding-left: 0;
    }
  }
}
</style>
